/**
 * Convert cron expressions to human-readable descriptions
 */
export function cronToHumanReadable(cronExpression: string): string {
  // Handle common patterns first
  const commonPatterns: Record<string, string> = {
    '*/1 * * * *': 'Every minute',
    '0 */1 * * * *': 'Every minute',
    '*/5 * * * *': 'Every 5 minutes',
    '0 */5 * * * *': 'Every 5 minutes',
    '*/10 * * * *': 'Every 10 minutes',
    '0 */10 * * * *': 'Every 10 minutes',
    '*/15 * * * *': 'Every 15 minutes',
    '0 */15 * * * *': 'Every 15 minutes',
    '*/30 * * * *': 'Every 30 minutes',
    '0 */30 * * * *': 'Every 30 minutes',
    '0 0 * * * *': 'Every hour',
    '0 0 */1 * * *': 'Every hour',
    '0 0 */2 * * *': 'Every 2 hours',
    '0 0 */6 * * *': 'Every 6 hours',
    '0 0 */12 * * *': 'Every 12 hours',
    '0 0 0 * * *': 'Daily at midnight',
    '0 0 12 * * *': 'Daily at noon',
    '0 2 * * *': 'Daily at 2:00 AM',
    '0 02 * * *': 'Daily at 2:00 AM',
    '0 0 * * 0': 'Weekly on Sunday',
    '0 0 * * 1': 'Weekly on Monday',
    '0 0 1 * *': 'Monthly on 1st',
    '0 20 * * 5': 'Weekly on Friday at 8:00 PM',
    '0 0-23/1 * * *': 'Every hour',
  };

  if (commonPatterns[cronExpression]) {
    return commonPatterns[cronExpression];
  }

  // Parse the cron expression for custom patterns
  const parts = cronExpression.trim().split(/\s+/);

  // Handle 6-part format (with seconds) vs 5-part format
  let second, minute, hour, day, month, dayOfWeek;

  if (parts.length === 6) {
    [second, minute, hour, day, month, dayOfWeek] = parts;
  } else if (parts.length === 5) {
    [minute, hour, day, month, dayOfWeek] = parts;
    second = '0';
  } else {
    return cronExpression; // Return original if can't parse
  }

  try {
    // Handle some specific patterns
    if (minute.startsWith('*/') && hour === '*' && day === '*' && month === '*' && dayOfWeek === '*') {
      const interval = minute.substring(2);
      return `Every ${interval} minute${interval === '1' ? '' : 's'}`;
    }

    if (minute === '0' && hour.startsWith('*/') && day === '*' && month === '*' && dayOfWeek === '*') {
      const interval = hour.substring(2);
      return `Every ${interval} hour${interval === '1' ? '' : 's'}`;
    }

    if (minute === '0' && hour === '0' && day.startsWith('*/') && month === '*' && dayOfWeek === '*') {
      const interval = day.substring(2);
      return `Every ${interval} day${interval === '1' ? '' : 's'}`;
    }

    // Handle specific times
    if (minute.match(/^\d+$/) && hour.match(/^\d+$/) && day === '*' && month === '*' && dayOfWeek === '*') {
      const h = parseInt(hour);
      const m = parseInt(minute);
      const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      return `Daily at ${time}`;
    }

    // Handle weekly patterns
    if (minute.match(/^\d+$/) && hour.match(/^\d+$/) && day === '*' && month === '*' && dayOfWeek.match(/^\d+$/)) {
      const h = parseInt(hour);
      const m = parseInt(minute);
      const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = days[parseInt(dayOfWeek)] || `Day ${dayOfWeek}`;
      return `Weekly on ${dayName} at ${time}`;
    }

    // Handle range patterns like 0-23/1
    if (hour.includes('/')) {
      const [range, interval] = hour.split('/');
      if (range.includes('-')) {
        const [start, end] = range.split('-');
        if (start === '0' && end === '23' && interval === '1') {
          return 'Every hour';
        }
      }
    }

    // Fallback to original expression
    return cronExpression;
  } catch (error) {
    // If parsing fails, return the original expression
    return cronExpression;
  }
}